<script>
  import { _ } from '@sveltia/i18n';
  import { Divider, Menu, MenuButton, MenuItem, Spacer, Toolbar } from '@sveltia/ui';
  import equal from 'fast-deep-equal';

  import CopyMenuItems from '$lib/components/contents/details/editor/copy-menu-items.svelte';
  import TranslateButton from '$lib/components/contents/details/editor/translate-button.svelte';
  import LocaleSwitcher from '$lib/components/contents/details/locale-switcher.svelte';
  import PreviewButton from '$lib/components/contents/details/preview-button.svelte';
  import PreviewLinkButton from '$lib/components/contents/details/preview-link-button.svelte';
  import { backend } from '$lib/services/backends';
  import { filterRealValues } from '$lib/services/contents/draft';
  import { getEntryDraftContext } from '$lib/services/contents/draft/state.svelte';
  import { toggleLocale } from '$lib/services/contents/draft/update/locale';
  import { revertChanges } from '$lib/services/contents/draft/update/revert';
  import { getValueMapSnapshot } from '$lib/services/contents/draft/value-map.svelte';
  import { getEntryRepoBlobURL } from '$lib/services/contents/entry';
  import { getLocaleLabel } from '$lib/services/contents/i18n';
  import { DEFAULT_I18N_CONFIG } from '$lib/services/contents/i18n/config';
  import { deployments, deployPollTimedOut, productionSHA } from '$lib/services/deployments';
  import { getEntryPreviewLink } from '$lib/services/deployments/link';
  import { recheckDeployments } from '$lib/services/deployments/poll';
  import { env } from '$lib/services/user/env.svelte';
  import { prefs } from '$lib/services/user/prefs.svelte';
  import { createRawState } from '$lib/services/utils/state.svelte';
  import { openNewTab } from '$lib/services/utils/window';
  import {
    getUnpublishedEntryBySlug,
    isPendingDeletion,
    workflowEnabled,
  } from '$lib/services/workflow';

  /**
   * @import { EntryEditorPane } from '$lib/types/private';
   */

  /**
   * @typedef {object} Props
   * @property {string} id The wrapper element’s `id` attribute.
   * @property {{ current: ?EntryEditorPane }} thisPane This pane’s mode and locale.
   * @property {{ current: ?EntryEditorPane }} [thatPane] Another pane’s mode and locale.
   */

  const entryDraft = getEntryDraftContext();

  /** @type {Props} */
  let {
    /* eslint-disable prefer-const */
    id,
    thisPane,
    thatPane = createRawState(null),
    /* eslint-enable prefer-const */
  } = $props();

  const collection = $derived(entryDraft.current?.collection);
  const collectionFile = $derived(entryDraft.current?.collectionFile);
  const originalEntry = $derived(entryDraft.current?.originalEntry);
  /* v8 ignore start -- the header is only rendered for a pane while the draft is there */
  const originalValues = $derived(entryDraft.current?.originalValues ?? {});
  const { i18nEnabled, saveAllLocales, allLocales, defaultLocale } = $derived(
    (collectionFile ?? collection)?._i18n ?? DEFAULT_I18N_CONFIG,
  );
  const isLocaleEnabled = $derived(
    entryDraft.current?.currentLocales[thisPane.current?.locale ?? ''],
  );
  const isOnlyLocale = $derived(
    Object.values(entryDraft.current?.currentLocales ?? {}).filter((enabled) => enabled).length ===
      1,
  );
  /* v8 ignore stop */
  const otherLocales = $derived(
    i18nEnabled ? allLocales.filter((l) => l !== thisPane.current?.locale) : [],
  );
  const canCopy = $derived(!!otherLocales.length);
  // Every option in the menu edits the content, which an entry awaiting deletion doesn’t allow
  const pendingDeletion = $derived(isPendingDeletion(entryDraft.current?.originalEntry));
  const canRevert = $derived(
    thisPane.current?.locale &&
      !equal(
        originalValues[thisPane.current.locale],
        // Exclude internal properties from the comparison
        filterRealValues(getValueMapSnapshot(entryDraft.current, thisPane.current.locale)),
      ),
  );
  /* v8 ignore next -- the header is only rendered for a pane while the draft is there */
  const canPreview = $derived(entryDraft.current?.canPreview ?? true);
  // Look the entry up in the store rather than reading the draft, so the preview link follows the
  // head commit as it moves with each save, the same way the entry toolbar does
  const pullRequest = $derived.by(() => {
    const collectionName = entryDraft.current?.collectionName;

    if (!workflowEnabled.current || !collectionName || !originalEntry) {
      return undefined;
    }

    const slug = entryDraft.current?.fileName ?? originalEntry.slug;

    return getUnpublishedEntryBySlug({ collectionName, slug })?.workflow.pullRequest;
  });
  // `PreviewLinkButton` renders nothing when there’s no link to offer, so the link is resolved
  // here as well — the divider above the button has to know whether anything will follow it
  /* v8 ignore start -- only read for an existing entry, once the pane is set up */
  const previewLink = $derived(
    originalEntry && collection && thisPane.current
      ? getEntryPreviewLink({
          entry: originalEntry,
          locale: thisPane.current.locale,
          collection,
          collectionFile,
          pullRequest,
          deployments: deployments.current,
          productionSHA: productionSHA.current,
          pollTimedOut: deployPollTimedOut.current,
        })
      : undefined,
  );
  /* v8 ignore stop */
</script>

<div role="none" {id} class="header">
  <Toolbar variant="secondary" ariaLabel={_('secondary')}>
    {#if i18nEnabled && allLocales.length > 1}
      <LocaleSwitcher {id} {thisPane} {thatPane} />
      {#if (env.isSmallScreen || env.isMediumScreen) && canPreview}
        <PreviewButton {thisPane} />
      {/if}
    {:else if !(env.isSmallScreen || env.isMediumScreen)}
      <h3 role="none">{thisPane.current?.mode === 'preview' ? _('preview') : _('edit')}</h3>
    {:else if canPreview}
      <PreviewButton {thisPane} />
    {/if}
    <Spacer flex />
    {#if thisPane.current?.mode === 'edit'}
      {@const localeLabel = getLocaleLabel(thisPane.current.locale) ?? thisPane.current.locale}
      {#if canCopy}
        <TranslateButton locale={thisPane.current.locale} {otherLocales} />
      {/if}
      <MenuButton
        variant="ghost"
        iconic
        disabled={pendingDeletion}
        popupPosition="bottom-right"
        aria-label={_('show_content_options_x_locale', { values: { locale: localeLabel } })}
      >
        {#snippet popup()}
          <Menu ariaLabel={_('content_options_x_locale', { values: { locale: localeLabel } })}>
            {#if canCopy && thisPane.current?.locale}
              <CopyMenuItems locale={thisPane.current.locale} {otherLocales} submenu />
            {/if}
            <MenuItem
              label={_('revert_changes')}
              disabled={!canRevert}
              onclick={() => {
                /* v8 ignore next 3 -- the menu is only offered while the draft is there */
                if (entryDraft.current) {
                  revertChanges({ draft: entryDraft.current, locale: thisPane.current?.locale });
                }
              }}
            />
            {#if !saveAllLocales && thisPane.current?.locale}
              <Divider />
              <MenuItem
                label={_(
                  isLocaleEnabled
                    ? 'disable_x_locale'
                    : entryDraft.current?.currentValues[thisPane.current.locale]
                      ? 'reenable_x_locale'
                      : 'enable_x_locale',
                  { values: { locale: localeLabel } },
                )}
                disabled={thisPane.current.locale === defaultLocale ||
                  (isLocaleEnabled && isOnlyLocale)}
                onclick={() => {
                  /* v8 ignore next 6 -- the menu is only offered for a pane with a locale */
                  if (entryDraft.current) {
                    toggleLocale({
                      draft: entryDraft.current,
                      locale: thisPane.current?.locale ?? '',
                    });
                  }
                }}
              />
            {/if}
            {#if originalEntry && collection && thisPane.current}
              {#if previewLink || deployPollTimedOut.current || prefs.devModeEnabled}
                <Divider />
              {/if}
              <PreviewLinkButton
                as="menuitem"
                entry={originalEntry}
                locale={thisPane.current.locale}
                {collection}
                {collectionFile}
                {pullRequest}
              />
              {#if deployPollTimedOut.current}
                <MenuItem
                  label={_('deploy_preview.check_again')}
                  onclick={() => {
                    recheckDeployments();
                  }}
                />
              {/if}
              {#if prefs.devModeEnabled}
                <MenuItem
                  disabled={!backend.current?.repository?.blobBaseURL}
                  label={backend.current?.repository?.label
                    ? _('view_on_x', { values: { service: backend.current.repository.label } })
                    : _('view_in_repository')}
                  onclick={() => {
                    /* v8 ignore next 3 -- the item is only offered for an existing entry */
                    if (originalEntry && thisPane.current) {
                      openNewTab(getEntryRepoBlobURL(originalEntry, thisPane.current.locale));
                    }
                  }}
                />
              {/if}
            {/if}
          </Menu>
        {/snippet}
      </MenuButton>
    {/if}
  </Toolbar>
</div>

<style>
  .header {
    flex: none !important;

    :global {
      & > .sui.toolbar {
        margin-inline: auto;
        max-width: 800px;

        @media (width < 768px) {
          padding: 0;
        }

        h3 {
          margin: 0 8px;
          font-size: var(--sui-font-size-default);
        }
      }
    }
  }
</style>
